import {
  OLSPredictionInput,
  OLSPredictionOutput,
  OLSAdminAction,
  OLSAdminActionResponse,
  OLSPricingModel,
  OLSModelVersion,
  OLSTrainingLog,
} from '@otw/models/ols'
import { axiosClient } from '../axiosClient'

export interface OLSPredictionBatchResponse {
  predictions: OLSPredictionOutput[]
}

export interface OLSModelAvailabilityResponse {
  available: boolean
  categoryId: string | null
}

export interface OLSCategoriesResponse {
  categories: OLSPricingModel[]
}

export interface OLSVersionsResponse {
  versions: OLSModelVersion[]
}

export interface OLSTrainingLogsResponse {
  logs: OLSTrainingLog[]
}

export const olsApi = {
  pricing: {
    /**
     * Predict price for a single vehicle
     */
    predictPrice: async (input: OLSPredictionInput): Promise<OLSPredictionOutput> => {
      return await axiosClient.post('/portal/pricing/predict-price', input)
    },

    /**
     * Predict prices for multiple vehicles (batch)
     */
    predictPriceBatch: async (inputs: OLSPredictionInput[]): Promise<OLSPredictionBatchResponse> => {
      return await axiosClient.post('/portal/pricing/predict-price-batch', { inputs })
    },

    /**
     * Check if OLS model exists for a category
     */
    checkModelAvailability: async (input: {
      make: string
      model: string
      type: string
      size: string
      fuel: 'Gas' | 'Diesel'
    }): Promise<OLSModelAvailabilityResponse> => {
      // Make a single prediction with dummy values to check availability
      const prediction = await olsApi.pricing.predictPrice({
        make: input.make,
        model: input.model,
        type: input.type,
        size: input.size,
        fuel: input.fuel,
        year: 2020,
        mileage: 100000,
      })

      return {
        available: !prediction.usedFallback && prediction.categoryId !== null,
        categoryId: prediction.categoryId,
      }
    },
  },

  admin: {
    /**
     * Get all OLS pricing model categories
     */
    getCategories: async (): Promise<OLSCategoriesResponse> => {
      return await axiosClient.get('/portal/pricing/ols/categories')
    },

    /**
     * Get version history for a category
     */
    getVersions: async (categoryId: string): Promise<OLSVersionsResponse> => {
      return await axiosClient.get(`/portal/pricing/ols/categories/${categoryId}/versions`)
    },

    /**
     * Get training logs
     */
    getTrainingLogs: async (limit: number = 50): Promise<OLSTrainingLogsResponse> => {
      return await axiosClient.get('/portal/pricing/ols/training-logs', { params: { limit } })
    },

    /**
     * Promote a model version to active
     */
    promoteVersion: async (categoryId: string, versionId: string): Promise<OLSAdminActionResponse> => {
      return await axiosClient.post('/portal/pricing/ols/admin-action', {
        action: 'promote' as OLSAdminAction,
        categoryId,
        versionId,
      })
    },

    /**
     * Rollback to a previous version
     */
    rollbackVersion: async (categoryId: string, versionId: string): Promise<OLSAdminActionResponse> => {
      return await axiosClient.post('/portal/pricing/ols/admin-action', {
        action: 'rollback' as OLSAdminAction,
        categoryId,
        versionId,
      })
    },

    /**
     * Archive a model version
     */
    archiveVersion: async (categoryId: string, versionId: string): Promise<OLSAdminActionResponse> => {
      return await axiosClient.post('/portal/pricing/ols/admin-action', {
        action: 'archive' as OLSAdminAction,
        categoryId,
        versionId,
      })
    },

    /**
     * Toggle auto-promote for a category
     */
    toggleAutoPromote: async (categoryId: string, enabled: boolean): Promise<OLSAdminActionResponse> => {
      return await axiosClient.post('/portal/pricing/ols/toggle-auto-promote', {
        categoryId,
        enabled,
      })
    },
  },
}
